import { PlusIcon, Info, ArrowRightIcon } from "lucide-react";
import { Button } from "@/shadcnComponents/ui/button";
import {
  BsPerson,
  BsPersonCheck,
  BsPersonPlus,
  BsCheckCircle,
  BsPencil,
} from "react-icons/bs";
import {
  CampaignReviewHeader,
  HighlightMetricsCard,
} from "../Components/Components";
import { LiveCampaignDraftTable } from "../Helpers/Helper";
import { LinkedinPostEditor } from "../CustomHooks/LinkedinEditor";
import {
  DraftRowAccordion,
  DraftRowHeader,
  DraftColInfluDetails,
  DraftColStatus,
} from "../Components/LiveCampaignComponents/LiveCampaignDraftComponents";
import {
  CollabStats,
  SelectedInfluencer,
} from "../CampaignInterface/LiveCampaignInterface";

interface LiveCampaignDraftsProps {
  selectedInfluencers: SelectedInfluencer[];
  collabStats: CollabStats;
  handleSubmitSuggestion: ({
    comment,
    images,
    editorState,
    isApprove,
  }: {
    comment: string;
    images: File[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editorState: any;
    isApprove?: boolean;
  }) => Promise<boolean>;
  handleApproveDraft: (influencerId: string) => Promise<void>;
  onClickMakeItLive: () => void;
}

export const LiveCampaignDrafts = ({
  selectedInfluencers,
  collabStats,
  handleSubmitSuggestion,
  handleApproveDraft,
  onClickMakeItLive,
}: LiveCampaignDraftsProps) => {
  return (
    <div className="flex w-full flex-col items-start  min-h-screen py-3 md:py-6 lg:py-12 px-[24px]">
      <div className="w-full flex justify-between">
        <div className="flex flex-col  gap-2 ">
          <h1 className="text-[#121212] font-[Inter] text-[18px] not-italic font-semibold leading-[normal]">
            Influencer Status & Drafts
          </h1>
          <p>
            Track influencer status, review drafts, and take action to keep your
            campaign moving
          </p>
        </div>
        {/* make it live button if any approved */}
        <Button
          variant="outline"
          className="text-[12px] flex items-center gap-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          onClick={() => {
            onClickMakeItLive();
          }}
          disabled={collabStats?.approvedDraftLength === 0}
        >
          Set Live Date
          <ArrowRightIcon size={20} />
        </Button>
      </div>

      {/* selected creator details */}
      <div className="flex w-full flex-col items-start mt-2 p-5 ">
        <CampaignReviewHeader Heading={"Details of Selected Influencers"} />

        <div
          className="grid grid-cols-2 md:flex flex-row gap-[15px] w-full overflow-x-auto py-2 px-1"
          style={{ scrollbarWidth: "none" }}
        >
          {[
            {
              icon: <BsPerson size={20} color="#1F6E03" />,
              title: "Selected influencers",
              value: selectedInfluencers?.length || `-`,
              bgColor: "bg-[#CFE8C6]",
            },
            {
              icon: <BsPersonCheck size={20} color="#AC0303" />,
              title: "Accepted",
              value: collabStats?.acceptedInfluencerLength || `-`,
              bgColor: "bg-[#FFE1E1]",
            },
            {
              icon: <BsPersonPlus size={20} color="#1070E0" />,
              title: "Draft Submitted",
              value: collabStats?.draftSubmittedInfluencerLength || `-`,
              bgColor: "bg-[#D5DBFF]",
            },
            {
              icon: <BsCheckCircle size={16} color="#05A197" />,
              title: "Draft Approved",
              value: collabStats?.approvedDraftLength || `-`,
              bgColor: "bg-[#42E3D833]",
            },
            {
              icon: <BsPencil size={16} color="#AD0F0F" />,
              title: "Suggested Changes",
              value: collabStats?.suggestedChangesLength || `-`,
              bgColor: "bg-[#FFE1E1]",
            },
          ]?.map((item, index) => (
            <div className="min-w-[200px] py-2">
              <HighlightMetricsCard
                key={index}
                icon={item?.icon}
                title={item?.title}
                value={item?.value}
                bgColor={item?.bgColor}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="w-full flex flex-col gap-[5px] p-5">
        <div className="flex">
          <div className={`w-full grid p-2 grid-cols-4`}>
            {LiveCampaignDraftTable?.map((item, index) => (
              <span
                className=" flex flex-wrap justify-start p-1  text-[color:var(--Natural-500,#9E9E9E)] text-sm not-italic font-semibold leading-5 "
                key={`row-item-${index}`}
              >
                {item}
              </span>
            ))}
          </div>
          <span className="whitespace-nowrap items-center flex flex-wrap justify-start p-1  text-[color:var(--Natural-500,#9E9E9E)] text-sm not-italic font-semibold leading-5 ">
            View More
          </span>
        </div>
        {selectedInfluencers?.map((Influencer, index) => (
          <div key={index}>
            <DraftRowAccordion
              Header={
                <DraftRowHeader
                  columnCount={4}
                  columnArray={[
                    //first Column
                    <DraftColInfluDetails Influencer={Influencer} />,
                    //second column
                    <p className="text-[color:var(--Natural-800,#424242)] text-sm not-italic font-semibold leading-5">
                      {Influencer?.influencerDetails?.collabCount > 0
                        ? "Yes"
                        : "No"}
                    </p>,
                    //third column
                    <DraftColStatus Influencer={Influencer} />,
                    //fourth column
                    <Button
                      variant="destructive"
                      className="text-[12px] flex items-center gap-1"
                    >
                      Check Draft
                      <PlusIcon size={20} />
                    </Button>,
                  ]}
                />
              }
              ContentArray={[
                <div className="p-4 flex flex-col gap-4">
                  {!Influencer?.draft?.length && (
                    <div
                      className={
                        "py-1 px-4 font-[Inter] text-[12px] not-italic font-semibold leading-[24px] rounded-[6px] uppercase bg-[#36D7CC33]/20 text-[#115E59] flex gap-2 items-center w-fit"
                      }
                    >
                      <Info fill="#115E59" color="#fff" />
                      NO DRAFT FOUND
                    </div>
                  )}
                  {Influencer?.draft?.map((draft, index) => {
                    return (
                      <LinkedinPostEditor
                        key={index}
                        initial={{
                          influencerName: Influencer?.influencerDetails?.name,
                          content: draft?.postText,
                          images: draft?.postImages,
                          videos: draft?.postVideos,
                          profileImage: Influencer?.influencerDetails?.profile,
                          tagline: Influencer?.influencerDetails?.tagline,
                          docid: draft?._id,
                          status: draft?.status,
                          draftStatus:
                            index === 0 ? "NEW DRAFT" : `OLD DRAFT - ${index}`,
                          influencerId: Influencer?.influencerDetails?._id,
                          suggestionComment: draft?.suggestedChanges?.comment,
                          suggestionImages: draft?.suggestedChanges?.images,
                          isApproved:
                            index === 0 &&
                            Influencer?.brandApproval === "accepted",
                          submittedAt: draft?.submittedAt,
                          suggestedAt: draft?.suggestedChanges?.suggestedAt,
                          approvedAt: Influencer?.approvedAt,
                        }}
                        onSubmitSuggestion={async ({
                          comment,
                          images,
                          editorState,
                          isApprove,
                        }) => {
                          const result = await handleSubmitSuggestion({
                            comment,
                            images,
                            editorState,
                            isApprove,
                          });
                          return result;
                        }}
                        onApproveDraft={() =>
                          handleApproveDraft(Influencer?.influencerDetails?._id)
                        }
                      />
                    );
                  })}
                </div>,
              ]}
            />
          </div>
        ))}
      </div>

      <div className="w-full items-center flex justify-center mt-auto">
        <Button
          variant="outline"
          className="text-[12px] flex items-center gap-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          onClick={() => {
            onClickMakeItLive();
          }}
          disabled={collabStats?.approvedDraftLength === 0}
        >
          Set Live Date
          <ArrowRightIcon size={20} />
        </Button>
      </div>
    </div>
  );
};
